import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { apiErrorText } from "@/features/sessions/errorMessages";
import { useToast } from "@/hooks/useToast";

import {
  useMe,
  useOrgs,
  useSetActiveOrg,
  type OrgView,
} from "@/api/hooks";

/** Active-org context for /admin pages.
 *
 * The org switcher persists its choice on the web-session row via
 * ``POST /api/auth/active-org``, so a full-page reload (or a new
 * tab on the same browser session) restores the same view. The
 * SPA's React-Query cache holds the slug — invalidations on
 * ``/auth/me`` + ``/sessions`` keep every consumer in sync.
 *
 * Visibility rules:
 * - Global admins (``user.role`` in admin/superadmin) see every org
 *   plus an "All organisations" option that clears the filter.
 * - Org admins/managers see only the orgs they have a role in.
 * - Plain members never reach /admin (the layout's gate hides it),
 *   so this context isn't visible to them at all. */
type OrgContextValue = {
  /** The slug currently driving config + branding queries.
   *  ``null`` = "All organisations" (global admins only). */
  slug: string | null;
  setSlug: (slug: string | null) => void;
  isPersisting: boolean;
  orgs: OrgView[];
  isLoading: boolean;
  /** Whether the caller can pick "All organisations" — global
   *  admins / superadmins only. Org-only admins are pinned to the
   *  set of orgs they belong to. */
  canSeeAllOrgs: boolean;
};


const OrgContext = React.createContext<OrgContextValue | null>(null);


export function OrgProvider(props: { children: React.ReactNode }): React.JSX.Element {
  const me = useMe();
  const orgs = useOrgs();
  const setActiveOrg = useSetActiveOrg();
  const toast = useToast();
  const { t } = useTranslation();

  const canSeeAllOrgs = Boolean(me.data?.admin_scope?.is_global_admin);
  const serverSlug = me.data?.admin_scope?.active_org_slug ?? null;

  const qc = useQueryClient();
  const setSlug = React.useCallback(
    (next: string | null) => {
      // A refused pin (deleted org, non-member) used to snap the switcher
      // back with no explanation.
      Promise.resolve(setActiveOrg.mutateAsync({ slug: next }))
        .then(() => {
          // The pin lives server-side and most admin queries are not
          // keyed by it, so an org switch used to leave every open tab
          // showing the previous org until a reload. Drop the whole
          // cache; the visible tab refetches for the new org.
          void qc.invalidateQueries();
        })
        .catch((err: unknown) => {
          toast(apiErrorText(t, err), "error");
        });
    },
    [setActiveOrg, toast, t, qc],
  );

  const value = React.useMemo<OrgContextValue>(
    () => ({
      slug: serverSlug,
      setSlug,
      isPersisting: setActiveOrg.isPending,
      orgs: orgs.data ?? [],
      isLoading: orgs.isLoading || me.isLoading,
      canSeeAllOrgs,
    }),
    [
      serverSlug,
      setSlug,
      setActiveOrg.isPending,
      orgs.data,
      orgs.isLoading,
      me.isLoading,
      canSeeAllOrgs,
    ],
  );

  return (
    <OrgContext.Provider value={value}>{props.children}</OrgContext.Provider>
  );
}


// eslint-disable-next-line react-refresh/only-export-components
// (hook + provider co-located on purpose — splitting just to make
// fast refresh happy would force every consumer to import from two
// files for no real-world gain.)
export function useActiveOrg(): OrgContextValue {
  const ctx = React.useContext(OrgContext);
  if (ctx === null) {
    return {
      slug: null,
      setSlug: () => undefined,
      isPersisting: false,
      orgs: [],
      isLoading: false,
      canSeeAllOrgs: false,
    };
  }
  return ctx;
}
